'use client';

import { cn } from '@/lib/cn';
import NextImage from 'next/image';
import type { Message } from '@repo/api';

interface AgentContactCardProps {
  message: Message;
}

interface InfoCardItem {
  image?: string;
  title?: string;
  text?: string[];
}

function isInfoCardItem(item: unknown): item is InfoCardItem {
  return (
    item !== null &&
    typeof item === 'object' &&
    ('image' in item || 'title' in item || 'text' in item)
  );
}

export function AgentContactCard({ message }: AgentContactCardProps) {
  // Get data from contentData as an array (matching InfoMessageCard structure)
  const contentData: unknown = message?.contentData;
  let data: InfoCardItem[] = [];

  if (Array.isArray(contentData)) {
    data = contentData.filter(isInfoCardItem);
  }

  if (data.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 ml-10 pr-12">
      {data.map((item: InfoCardItem, index: number) => {
        const image = item?.image;
        const title = item?.title || '';
        const textArray = Array.isArray(item?.text) ? item.text : [];

        return (
          <div
            key={index}
            className={cn(
              'self-start min-w-[80%] rounded-3xl p-3 bg-secondary',
              'flex items-start justify-start gap-3 mb-1',
            )}
          >
            {/* Image */}
            {image && (
              <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0 overflow-hidden">
                <NextImage
                  src={image}
                  alt={title}
                  width={44}
                  height={44}
                  className="rounded-full object-cover"
                />
              </div>
            )}

            {/* Title and Text */}
            <div className="flex-1 min-w-0 flex flex-col gap-y-1">
              <h3 className="text-body-1-high text-primary font-semibold">{title}</h3>
              {textArray.length > 0 &&
                textArray.map((text: string, textIndex: number) => (
                  <p key={textIndex} className="text-body-2 text-secondary">
                    {text}
                  </p>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
